import React, { useMemo } from 'react'
import AddIcon from '@mui/icons-material/Add'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import EditIcon from '@mui/icons-material/Edit'
import HistoryIcon from '@mui/icons-material/History'

import Button from 'components/Form/Button'
import Table from 'components/Form/Table'
import useI18n from 'hooks/useI18n'
import { toDateLabel } from './date'
import {
  ActionRow,
  EmptyAction,
  EmptyGraphic,
  EmptyState,
  EmptyTitle,
  IconButton,
  LinkText,
  StatusPill,
  TableSurface,
  TitleText,
} from './styled'

export default function InvitesTable({ invites, loading, onCreate, onCopy, onEdit, onHistory }) {
  const { t } = useI18n()

  const columns = useMemo(() => [
    {
      title: t('staff_essentials_invites_column_title'),
      ref: 'title',
      renderCell: ({ row }) => <TitleText title={row.title || row.invite_code}>{row.title || row.invite_code || '-'}</TitleText>,
    },
    {
      title: t('staff_essentials_invites_column_link'),
      ref: 'link',
      renderCell: ({ row }) => <LinkText title={row.link}>{row.link || '-'}</LinkText>,
    },
    {
      title: t('staff_essentials_invites_column_company'),
      ref: 'company_name',
      renderCell: ({ row }) => <TitleText title={row.company_name}>{row.company_name || '-'}</TitleText>,
    },
    {
      title: t('staff_essentials_invites_column_status'),
      ref: 'is_active',
      renderCell: ({ row }) => (
        <StatusPill active={row.is_active}>
          {t(row.is_active ? 'staff_essentials_invites_active_status' : 'staff_essentials_invites_inactive_status')}
        </StatusPill>
      ),
    },
    {
      title: t('staff_essentials_invites_column_used'),
      ref: 'uses',
      renderCell: ({ row }) => row.uses,
    },
    {
      title: t('staff_essentials_invites_column_remaining'),
      ref: 'remaining_uses',
      renderCell: ({ row }) => row.remaining_uses == null ? t('staff_essentials_invites_unlimited') : row.remaining_uses,
    },
    {
      title: t('staff_essentials_invites_column_expires'),
      ref: 'expires_at',
      renderCell: ({ row }) => row.access_duration_days ? `${row.access_duration_days} ${t('lite_invite_days')}` : toDateLabel(row.expires_at),
    },
    {
      title: t('staff_essentials_invites_column_created'),
      ref: 'createdAt',
      renderCell: ({ row }) => toDateLabel(row.createdAt),
    },
    {
      title: t('staff_essentials_invites_column_actions'),
      ref: 'actions',
      renderCell: ({ row }) => (
        <ActionRow>
          <IconButton title={t('staff_essentials_invites_copy_link')} onClick={() => onCopy(row)} disabled={!row.link}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
          <IconButton title={t('staff_essentials_invites_history')} onClick={() => onHistory(row)}>
            <HistoryIcon fontSize="small" />
          </IconButton>
          <IconButton title={t('staff_essentials_invites_edit')} onClick={() => onEdit(row)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </ActionRow>
      ),
    },
  ], [onCopy, onEdit, onHistory, t])

  if (!loading && !invites.length) {
    return (
      <EmptyState>
        <EmptyGraphic>+</EmptyGraphic>
        <EmptyTitle>{t('staff_essentials_invites_empty_title')}</EmptyTitle>
        <div>{t('staff_essentials_invites_empty_description')}</div>
        <EmptyAction>
          <Button color="primary" width="fit-content" startIcon={<AddIcon />} onClick={onCreate}>
            {t('staff_essentials_invites_create')}
          </Button>
        </EmptyAction>
      </EmptyState>
    )
  }

  return (
    <TableSurface>
      <Table columns={columns} rows={invites} loading={loading} pagination={invites.length > 10} />
    </TableSurface>
  )
}
